const _ = require('lodash');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const OnlineQueueRunner = require('./OnlineQueueRunner');
const OnlineTask = require('./OnlineTask');
const OnlineTaskFailedError = require('./OnlineTaskFailedError');
const OnlineTaskStatus = require('@/lib/OnlineQueue/OnlineTaskStatus');
const OnlineTaskTrigger = require('@/lib/OnlineQueue/OnlineTaskTrigger');
const { findNonRecoverableErrors } = require('@/lib/Orders/orderErrorFunctions');
const { timeoutPromise } = require('@/functions/functions');

/**
 * OnlineQueue accepts tasks and runs them only when the device is online.
 * Queued tasks are stored in an IndexedDB table.
 * Various different types of tasks can be added (see the OnlineTaskType enum). Each task has a 'handler' that is
 * called for that task type.
 *
 * Handlers:
 * Each task type needs a function supplied that "handles" that task. Add a handler for a task type with the
 * registerHandler() method.
 * The handler will be invoked with an OnlineTask object and any dependencies will be injected.
 * The handler should return a promise.
 * It is important to remember that the handler function might not run immediately when the user performs an action.
 * This means the handler should not be performing any UI updates as these do not make sense if the handler runs later.
 * Handlers should be registered as early as possible in the application lifecycle. Historically handlers had been
 * registered when individual services boot, but this has been changed so each handler is a completely separate function
 * that can have dependencies injected and so can be registered without its dependencies being constructed yet.
 *
 * Adding tasks:
 * Add a task with the addTask() method. This returns a promise containing an OnlineTask object.
 * If the device is online when a task is added the handler will be called immediately. If not the task will go into
 * the queue table and be run at a later time once the device is online.
 * The result of a call to addTask is a promise containing an OnlineTask. The code that calls addTask should update
 * the UI once this promise resolves. It can tell if the task has actually run by checking the `hasRun` method of
 * the task object. If it has already run the return value of the handler will be set as the task's `result` property.
 * Using redemption as an example, when the user clicks the redeem button the addTask() method is called. When the
 * promise is resolved if the returned task has `hasRun` = true it will also have a `result` value that can be used
 * to update the UI with the new true redemption count from the API. If the promise resolves and `hasRun` = false
 * the code should assume it will be run at a later time, but it needs to update the UI now so the user sees something
 * has happened. It could increment the redemption count by 1 and show it as redeemed.
 */
class OnlineQueue {
    /**
     * @param $injector
     * @param {ConnectivityChecker} connectivityChecker
     * @param {OnlineTaskRepository} onlineTaskRepository
     * @param {UserService} userService
     */
    constructor(
        $injector,
        connectivityChecker,
        onlineTaskRepository,
        userService,
    ) {
        this.$injector = $injector;
        this.connectivityChecker = connectivityChecker;
        this.onlineTaskRepository = onlineTaskRepository;
        this.userService = userService;

        this.queueRunner = new OnlineQueueRunner(
            connectivityChecker,
            this,
        );

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('OnlineQueue');

        /**
         * @type {Object<string, Function>}
         */
        this.handlers = {};

        /**
         * @type {number}
         */
        this.maxTaskAttempts = 5;

        /**
         * Wait this many ms before processing the queue at startup or when reconnected.
         *
         * @type {number}
         */
        this.startUpDelay = 1000;

        /**
         * If the queue is currently processing tasks this will be a promise.
         *
         * @type {Promise|null}
         */
        this.runPromise = null;

        /**
         * Functions to be called when the queue has finished processing everything it contains.
         *
         * @type {Array}
         */
        this.finishedListeners = [];

        /**
         * After how many milliseconds can we assume a task with running status has failed?
         *
         * @type {Number}
         */
        this.maxRunningTaskDuration = 1000 * 60 * 1; // 1 minute.
    }

    init() {
        this.connectivityChecker.addObserver(
            {
                key: 'onlinequeue',
                notifyOnline: () => {
                    this.log.debug('Now online');
                    timeoutPromise(this.startUpDelay)
                        .then(() => this.checkRunningTasks())
                        .then(() => this.run());
                },
                notifyOffline: () => {
                    this.log.debug('Now offline');
                },
            },
            true,
        );

        // This observer is called every time we make an API status request and the device is online.
        // We check if there are any queued tasks and if the queue is not already running we run the queue this will
        // pick up things like retry tasks created when there is an online error.
        this.connectivityChecker.addStatusObserver(
            'onlinequeue',
            () => {
                this.countQueuedTasks().then((count) => {
                    if (count) {
                        if (this.runPromise) {
                            this.log.debug('Device online: Queue is already running');
                        } else {
                            this.log.debug('Device online: Queue is not currently running');
                            // We have items in the queue but the queue is not running at the moment.
                            let runPromise = this.queueRunner.runWithoutOnlineCheck();
                            this.runPromise = runPromise;
                            runPromise.finally(() => {
                                this.runPromise = null;
                                this.fireOnFinishedListenersIfNoTasks();
                            });
                        }
                    }
                });
            }
        );
    }

    run() {
        let runPromise = this.queueRunner.runWithOnlineCheck();
        this.runPromise = runPromise;
        runPromise.finally(() => {
            this.runPromise = null;
            this.fireOnFinishedListenersIfNoTasks();
        });
    }

    /**
     * @param {string} type One of OnlineTaskType.
     * @param {{}} data
     * @param {string} description
     * @param {?string} [onlineTaskTrigger]
     *
     * @return {OnlineTask}
     */
    createTask(type, data, description, onlineTaskTrigger) {
        if (!onlineTaskTrigger) {
            onlineTaskTrigger = this.getCurrentTaskTrigger();
        }

        const task = new OnlineTask();
        task.createdAt = new Date();
        task.type = type;
        task.status = OnlineTaskStatus.PENDING;
        task.data = data;
        task.apiKey = this.userService.getApiKey();
        task.description = description;
        task.onlineTaskTrigger = onlineTaskTrigger;

        return task;
    }

    /**
     * Add a task to the queue. If the device is online it runs immediately, if not it will be queued.
     *
     * Note that the promise returned resolves immediately and the task may or may not have been run yet. Use the
     * returned OnlineTask's hasRun and result properties to check if the task has actually run online. If these
     * are false/null the device is offline and the task will be run later.
     *
     * @param {OnlineTask} task
     *
     * @returns {Promise<OnlineTask>}
     */
    addTask(task) {
        if (!this.hasHandler(task.type)) {
            throw new Error(`OnlineQueue doesn't know how to handle ${task.type}`);
        }

        return this.queueTask(task)
            .then(() => {
                if (this.connectivityChecker.isOnline()) {
                    // Run the task now.
                    // We return a promise which always resolves, because the result of the
                    // task being run is stored on the task itself. This promise is being returned in response to
                    // a request to add the task to the queue, which has succeeded at this point. It should not
                    // reject if running the task failed, because it was still added to the queue as requested.
                    return new Promise((resolve) => {
                        this.runTask(task)
                            .then(() => resolve(task))
                            .catch(() => resolve(task));
                    });
                }
                return task;
            });
    }

    /**
     * @private
     *
     * @param {OnlineTask} task
     *
     * @return {Promise<OnlineTask>}
     */
    queueTask(task) {
        return this.onlineTaskRepository.persist(task)
            .then((ID) => {
                task.ID = ID;
                this.log.debug(`Queued ${task.type} task ${task.ID}`);

                return task;
            });
    }

    /**
     * @private
     *
     * @param {OnlineTask} task
     *
     * @return {Promise<OnlineTask>}
     */
    async runTask(task) {
        this.log.debug(`Running ${task.type} task`, task);

        let handler = this.getHandler(task.type);

        ++task.attempts;

        // Set task as running in the queue table. Wait for that to persist before we start working on it.
        // TODO: Can we check the result of the persist here to make sure it was updated.
        // If it did not it's possible another instance is running it at the same time.
        task.status = OnlineTaskStatus.RUNNING;
        task.lastRunAt = new Date();
        await this.onlineTaskRepository.persist(task).catch(() => {
        });

        return this.$injector
            .invoke(
                handler,
                this,
                {
                    task,
                },
            )
            .then(async (result) => {
                // Success
                task.status = OnlineTaskStatus.FINISHED;
                task.result = result;

                // Remove from the queue table.
                await this.onlineTaskRepository
                    .remove(task.ID)
                    .catch(() => {
                    });

                return task;
            })
            .catch(async (result) => {
                // Failure
                ++task.failures;

                // Only store a few parts of the result because this might be an angular resource failure
                // result which includes some functions and can't be stored in indexeddb.
                task.errors.push(
                    _.pick(
                        result,
                        'data',
                        'responseData',
                        'status',
                        'statusCode',
                        'message'
                    )
                );

                this.log.warn('Task failed. ' + JSON.stringify(task));

                const lastError = task.errors[task.errors.length - 1];

                // lastError can either be a multipart object or simple object containing only the message string if it
                // is coming from apiResponseToError() which can happen when there is an error submitting an order that was
                // created when ProPoint was offline.
                if (
                    (lastError.data && lastError.data.error && findNonRecoverableErrors(lastError.data.error))
                    || (lastError.message && findNonRecoverableErrors(lastError.message))
                ) {
                    this.log.error('Task permanently failed skipping retry. ' + JSON.stringify(task));
                    task.status = OnlineTaskStatus.FAILED;
                }

                if (task.status !== OnlineTaskStatus.FAILED) {
                    if (task.skippable) {
                        this.log.error('Task is skippable - skipping. ' + JSON.stringify(task));

                        // Remove from the queue table.
                        await this.onlineTaskRepository
                            .remove(task.ID)
                            .catch(() => {
                            });

                        // Throw the exception here to stop further execution.
                        throw new OnlineTaskFailedError(task);
                    } else if (task.attempts >= this.maxTaskAttempts) {
                        this.log.error('Task failed ' + this.maxTaskAttempts + ' times. ' + JSON.stringify(task));
                        task.status = OnlineTaskStatus.FAILED;
                    } else {
                        // Mark the task as retryable.
                        task.status = OnlineTaskStatus.PENDING;

                        switch (task.onlineTaskTrigger) {
                            case OnlineTaskTrigger.OFFLINE:
                                task.onlineTaskTrigger = OnlineTaskTrigger.OFFLINE_RETRY;
                                break;
                            case OnlineTaskTrigger.ONLINE:
                                task.onlineTaskTrigger = OnlineTaskTrigger.ONLINE_RETRY;
                                break;
                        }

                        // If authentication failed attempt to substitute the api key for the next attempt
                        if (
                            result
                            && (result.status === 401 || result.statusCode === 401)
                            && this.userService.getApiKey()
                            && this.userService.getApiKey() !== task.apiKey
                        ) {
                            // Re-run the task with a different API key.
                            task.apiKey = this.userService.getApiKey();
                        }
                    }
                }

                // Update the task in the queue, either marking it as failed or retryable.
                await this.onlineTaskRepository
                    .persist(task)
                    .catch(() => {
                    });

                throw new OnlineTaskFailedError(task);
            });
    }

    /**
     * @returns {Promise<?OnlineTask>}
     */
    getNextTask() {
        return this.onlineTaskRepository.findNextPendingTask();
    }

    /**
     * Returns the total number of tasks that are not resolved.
     * This means those that are pending, currently running, or finished.
     * Note that finished tasks are deleted upon completion, so those won't stick around long.
     *
     * @returns {Promise<number>}
     */
    countQueuedTasks() {
        return this.onlineTaskRepository.countQueued();
    }

    /**
     * @returns {Promise<number>}
     */
    countAllTasks() {
        return this.onlineTaskRepository.countAll();
    }

    /**
     * @returns {Promise<number>}
     */
    countRunningTasks() {
        return this.onlineTaskRepository.countRunning();
    }

    /**
     * @returns {Promise<number>}
     */
    countPendingTasks() {
        return this.onlineTaskRepository.countPending();
    }

    /**
     * @returns {Promise<number>}
     */
    countFailedTasks() {
        return this.onlineTaskRepository.countFailed();
    }

    async getQueueAsJson() {
        const tasks = await this.onlineTaskRepository.findAllAsArray();
        return '{"queue": ' + JSON.stringify(tasks) + '}';
    }

    /**
     * @param {string} type
     * @param {Function} handler
     */
    registerHandler(type, handler) {
        if (typeof handler !== 'function') {
            throw new Error(`handler for ${type} must be a function.`);
        }

        this.handlers[type] = handler;
    }

    /**
     * @param {string} type
     *
     * @returns {Function}
     */
    getHandler(type) {
        if (!this.hasHandler(type)) {
            throw new Error(`No handler is registered for ${type}`);
        }

        return this.handlers[type];
    }

    /**
     * @param {string} type
     *
     * @returns {boolean}
     */
    hasHandler(type) {
        return this.handlers.hasOwnProperty(type);
    }

    /**
     * @param {Function} callback
     */
    onFinish(callback) {
        this.finishedListeners.push(callback);

        if (!this.runPromise) {
            // If the queue were currently running it would fire the callbacks when it finished.
            // But it's not running right now so the queue might be empty already. Fire the callbacks now
            // if the table is empty.
            this.fireOnFinishedListenersIfNoTasks();
        }
    }

    /**
     * Same as onFinish but can be awaited.
     *
     * @returns {Promise}
     */
    async onFinishPromise() {
        return new Promise((resolve,) => {
            this.finishedListeners.push(resolve);

            if (!this.runPromise) {
                // If the queue were currently running it would fire the callbacks when it finished.
                // But it's not running right now so the queue might be empty already. Fire the callbacks now
                // if the table is empty.
                this.fireOnFinishedListenersIfNoTasks();
            }
        });
    }

    /**
     * @private
     */
    fireOnFinishedListenersIfNoTasks() {
        // Check if there are any queued items.
        this.countQueuedTasks()
            .then((count) => {
                if (count < 1) {
                    this.fireOnFinishedListeners();
                }
            });
    }

    /**
     * @private
     */
    fireOnFinishedListeners() {
        this.finishedListeners.forEach((cb) => cb());
        this.finishedListeners = [];
    }

    checkRunningTasks() {
        return this.onlineTaskRepository.findAllRunning().then((runningTasks) => {
            this.log.debug(`Checking ${runningTasks.length} running task(s)`);
            runningTasks.forEach(async (task) => {
                if (
                    // Check if the task is a currently running task or an old task that has got stuck.
                    // Older queued tasks do not have a lastRunAt property at all.
                    !task.hasOwnProperty('lastRunAt')
                    || !task.lastRunAt
                    || task.lastRunAt < (Date.now() - this.maxRunningTaskDuration)
                ) {
                    // Reset the status to pending so we can attempt to run it again so it can either succeed or fail.
                    task.status = OnlineTaskStatus.PENDING;
                    await this.onlineTaskRepository.persist(task).catch(() => {});
                    this.log.debug('Updated task ' + JSON.stringify(task));
                }
            });
            return true;
        });
    }

    /**
     * @returns {string}
     */
    getCurrentTaskTrigger() {
        return this.connectivityChecker.isOnline() ? OnlineTaskTrigger.ONLINE : OnlineTaskTrigger.OFFLINE;
    }
}

module.exports = OnlineQueue;
